'use client';

/**
 * Forked from apps/users/application/TwoFactor/TwoFactorAuthenticationContext.tsx so CFR's 2FA
 * alerts go through @creditflowresearch/uilib/Alert instead of @dealroadshow/uikit-alert.
 * All auth logic (TwoFactorRepository/SessionService/SSORepository calls) is unchanged and must
 * stay in sync with the apps/users original by hand — there is no shared source of truth.
 * App Router migration: apps/allocate/app/layout.tsx imports @/users/ui/components/
 * TwoFactorAuthentication (the shared, unmodified one) directly — do NOT do that for CFR;
 * keep pointing at this fork via ./TwoFactorAuthentication.tsx.
 */
import React, { useState } from 'react';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import TwoFactorRepository from '@users/two-factor-authentication/TwoFactorRepository';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import usersUrl from '@users/url';
import { SessionService } from '@users/session/SessionService';
import { getContainer } from '@framework/di/getContainer';
import Logger from '@framework/sentry/Logger';
import SSORepository from '@users/sso/SSORepository';
import { domainRegexp } from '@framework/ui/Organisms/FinalForm/validators/validateDomain';
import { TwoFactorAuthenticationContext } from '@users/two-factor-authentication/TwoFactorAuthenticationContext';

interface IPasswordFormData {
  password: string,
}

interface IEmailFormData {
  email: string,
}

interface IPhoneVerificationFormData {
  verificationPhone: {
    value: string,
    code: string,
  },
}

export type DeliveryMethodType = 'phone' | 'email';

const useTwoFactorAuthentication = () => {
  const container = getContainer();
  const [isFetching, setIsFetching] = useState<boolean>(false);
// @ts-ignore TODO: FIN-32798 strictNullChecks
  const [errorMessage, setErrorMessage] = useState<string>(null);

  const checkPassword = async (
    router: {
      push: (url: string) => void,
      replace: (url: string) => void,
      pathname: string,
      asPath: string,
      query: { [key: string]: string },
    },
    { password }: IPasswordFormData,
  ): Promise<boolean | IPasswordFormData> => {
    const sessionService: SessionService = container.get(SessionService);
    const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);

    const { replace } = router;
    // @ts-ignore
    const { mobileTwoFactor } = sessionService.currentUser;

    const pathname = `/two_factor_authentication/${ mobileTwoFactor ? 'turn_off' : 'add_phone' }`;

    try {
      const { updateToken } = await twoFactorRepository.generateTwoFactorToken(password);
      replace(`${ pathname }/${ updateToken }`);
    } catch (error) {
      if (error instanceof RpcError) {
        if (error.getCode() === ErrorCodeHelper.getCodeByName('USER_INVALID_PASSWORD')) {
          return { password: getErrorMessage(error) };
        }
      }

      AlertManager.error(getErrorMessage(error));
    }

    return true;
  };

  const ssoLoginVerification = async (
    router: {
      push: (url: string) => void,
      replace: (url: string) => void,
      pathname: string,
      asPath: string,
      query: { [key: string]: string },
    },
    { email }: IEmailFormData,
  ): Promise<void> => {
    const { replace, query: { callbackUrl } } = router;

    try {
      const domain = email.split('@')[1];
      if (!domain || !domainRegexp.test(domain)) return;
      const ssoRepository = container.get(SSORepository);
      const resp = await ssoRepository.checkIsSSOEmail({ domain });
      if (resp) {
        window.location.href = usersUrl.getSsoLoginUrl(email, callbackUrl);
      } else {
        replace('/profile');
      }
    } catch (error) {
      replace('/profile');
      Logger.error({ message: 'Two-factor authentication failed', error });
    }
  };

  const addPhone = async (
    router: {
      replace: (url: string) => void,
      asPath: string,
    },
    token: string,
    { verificationPhone }: IPhoneVerificationFormData,
  ): Promise<void> => {
    const { replace } = router;
    const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);
    const sessionService: SessionService = container.get(SessionService);

    const { value: phone } = verificationPhone;
    try {
      await twoFactorRepository.addTwoFactorPhone(phone, token);
      await sessionService.getCurrentUser({ clearCache: true });
      replace('/two_factor_authentication/verifying_phone');
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    }
  };

  const checkCode = async (code: string, successCallback: () => void): Promise<void> => {
    const sessionService: SessionService = container.get(SessionService);

    setIsFetching(true);
// @ts-ignore TODO: FIN-32798 strictNullChecks
    setErrorMessage(null);

    try {
      const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);
      await twoFactorRepository.checkTwoFactorCode(code);
      await sessionService.getCurrentSession({ clearCache: true });
      successCallback();
    } catch (error) {
      handleTwoFactorAuthenticationError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const sendCode = async (deliveryMethod: DeliveryMethodType): Promise<void> => {
    setIsFetching(true);
// @ts-ignore TODO: FIN-32798 strictNullChecks
    setErrorMessage(null);

    try {
      const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);
      await twoFactorRepository.sendTwoFactorCode(deliveryMethod);
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  };

  const enableMobileTwoFactor = async (code: string, successCallback: () => void) => {
    const sessionService: SessionService = container.get(SessionService);

    setIsFetching(true);
// @ts-ignore TODO: FIN-32798 strictNullChecks
    setErrorMessage(null);

    try {
      const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);
      await twoFactorRepository.enableMobileTwoFactor(code);
      await sessionService.getCurrentUser({ clearCache: true });
      successCallback();
    } catch (error) {
      handleTwoFactorAuthenticationError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const turnOff = async (token: string, successCallback: () => void) => {
    const sessionService: SessionService = container.get(SessionService);

    setIsFetching(true);
// @ts-ignore TODO: FIN-32798 strictNullChecks
    setErrorMessage(null);

    try {
      const twoFactorRepository: TwoFactorRepository = container.get(TwoFactorRepository);
      await twoFactorRepository.disableMobileTwoFactor(token);
      await sessionService.getCurrentUser({ clearCache: true });
      successCallback();
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setIsFetching(false);
    }
  };

  const resetTwoFactorData = (): void => {
    setIsFetching(false);
// @ts-ignore TODO: FIN-32798 strictNullChecks
    setErrorMessage(null);
  };

  const handleTwoFactorAuthenticationError = (error: unknown): void => {
    const twoFactorErrorCodes = [
      ErrorCodeHelper.getCodeByName('TWO_FACTOR_CODE_EXPIRED'),
      ErrorCodeHelper.getCodeByName('TWO_FACTOR_CODE_INVALID_VALUE'),
      ErrorCodeHelper.getCodeByName('TWO_FACTOR_FAIL_ATTEMPT'),
    ];

// @ts-ignore TODO: FIN-32798 strictNullChecks
    if (error instanceof RpcError && twoFactorErrorCodes.includes(error.getCode())) {
      setErrorMessage(getErrorMessage(error));
    } else {
      AlertManager.error(getErrorMessage(error));
    }
  };

  return {
    isFetching,
    errorMessage,
    checkPassword,
    ssoLoginVerification,
    addPhone,
    checkCode,
    sendCode,
    enableMobileTwoFactor,
    turnOff,
    resetTwoFactorData,
  };
};

interface IProps {
  children: React.ReactNode,
}

const TwoFactorAuthenticationContextProvider = ({ children }: IProps) => (
  <TwoFactorAuthenticationContext.Provider value={ { ...useTwoFactorAuthentication() } }>
    { children }
  </TwoFactorAuthenticationContext.Provider>
);

export default TwoFactorAuthenticationContextProvider;
